import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Star, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { calculateSalePrice } from '../utils/calculateSalePrice';
import { getRegionPrice } from '../utils/getRegionPrice';
import { LandingTrustStrip, LandingFeatureGrid, LandingCTABand } from '../components/sections/LandingSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEGMENT_CONFIG = {
  entrepreneur: {
    eyebrow: 'Business & Entrepreneur',
    title: 'D-Freeze Entrepreneur',
    tagline: 'Freeze drying built for entrepreneurs and growing food businesses.',
    description:
      'Compact and mid-capacity systems for startups, small producers, restaurants and agribusinesses ready to build a product line. Preserve ingredients, create shelf-stable products, and lock in nutrition and flavour for up to 25 years.',
    highlights: [
      'Countertop to mid-size production capacity',
      'Simple, automated operation for lean teams',
      'Ideal for food startups, producers & restaurants',
      'Financing options to help you scale'
    ]
  },
  'commercial-scientific': {
    eyebrow: 'Commercial & Scientific',
    title: 'D-Freeze Commercial & Scientific',
    tagline: 'High-throughput freeze drying for commercial production and scientific research.',
    description:
      'Heavy-duty, high-capacity platforms built to North American electrical and safety codes. Engineered for food manufacturers, nutraceutical and pharmaceutical producers, and research labs that need reliable volume and repeatable results.',
    highlights: [
      'Maximum capacity, built for volume',
      'Commercial-grade vacuum & cooling systems',
      'Training and installation options available',
      'Business-level service support to prevent downtime'
    ]
  }
};

const ProductLine = ({ segment }) => {
  const config = SEGMENT_CONFIG[segment] || SEGMENT_CONFIG.entrepreneur;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { formatPrice, formatMoney, convertPrice, currency } = useCurrency();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        await axios.post(`${API_URL}/api/seed`).catch(() => {});
        const res = await axios.get(`${API_URL}/api/products`);
        const all = res.data || [];
        setProducts(all.filter(p => (p.segment || 'entrepreneur') === segment));
      } catch (e) {
        console.error('Error fetching product line:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [segment]);

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1, { currency });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`product-line-${segment}`}>
      <SEO
        title={config.title}
        description={config.description}
        keywords={`${config.title}, ${config.eyebrow}, freeze dryer, D-Freeze`}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-44 pb-16 bg-gradient-to-br from-[#003456] via-[#003456] to-[#002B47] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[32rem] h-[32rem] bg-[#CE8946] rounded-full blur-3xl opacity-20 translate-x-1/3 -translate-y-1/3"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em] mb-4">
                {config.eyebrow}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5">
                {config.title}
              </h1>
              <p className="text-lg text-white/85 mb-6 max-w-xl">{config.tagline}</p>
              <p className="text-base text-white/70 mb-8 max-w-xl">{config.description}</p>
              <div className="flex flex-wrap gap-4">
                <a href="#lineup" className="inline-flex items-center gap-2 bg-[#CE8946] text-[#0a0203] font-semibold px-6 py-3 rounded-md hover:bg-[#00a9b1] transition-colors" data-testid="view-lineup-btn">
                  View the Lineup <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 transition-colors">
                  Talk to Sales
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {config.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-3">
                  <span className="w-8 h-8 rounded-full bg-[#CE8946] flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-[#0a0203]" />
                  </span>
                  <span className="text-white/90 text-sm font-medium">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingTrustStrip />

      {/* Product grid */}
      <section id="lineup" className="py-16 section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-[#0a0203]">Models in this line</h2>
            <Link to={`/products?segment=${segment}`} className="text-[#003456] font-semibold hover:underline flex items-center gap-1">
              See all in catalogue <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading models…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl">
              <p className="text-gray-600">No models in this line yet. Check back soon.</p>
              <Link to="/products" className="text-[#003456] font-semibold hover:underline mt-2 inline-block">Browse all products</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => {
                const saleInfo = calculateSalePrice(product, currency);
                return (
                  <Link key={product.id} to={`/products/${product.id}`} className="group" data-testid={`line-product-${product.id}`}>
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover">
                      <div className="aspect-square bg-gray-100 overflow-hidden relative">
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        {!product.in_stock && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="bg-[#CE8946] text-white px-4 py-2 rounded-md font-medium">Out of Stock</span>
                          </div>
                        )}
                        {product.size && (
                          <span className="absolute top-3 left-3 bg-[#0a0203] text-white px-2 py-1 rounded text-xs font-medium">{product.size}</span>
                        )}
                        <button
                          onClick={(e) => handleAddToCart(e, product)}
                          disabled={!product.in_stock}
                          className="absolute bottom-3 right-3 bg-[#003456] text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-[#002B47] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                          data-testid={`line-add-to-cart-${product.id}`}
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                          ))}
                          <span className="text-xs text-gray-500 ml-1">(127)</span>
                        </div>
                        <h3 className="font-semibold text-[#0a0203] line-clamp-1">{product.name}</h3>
                        <div className="flex items-baseline gap-2 mt-2">
                          {saleInfo.isOnSale && saleInfo.effectivePrice ? (
                            <>
                              <span className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>{formatMoney(saleInfo.effectivePrice)}</span>
                              <span className="text-sm text-gray-400 line-through" style={{ fontFamily: 'JetBrains Mono' }}>{formatMoney(saleInfo.originalPrice)}</span>
                            </>
                          ) : (
                            <span className="text-xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>{formatMoney(getRegionPrice(product, currency))}</span>
                          )}
                          {!saleInfo.isOnSale && (
                            <span className="text-xs text-gray-500">or ${Math.round(getRegionPrice(product, currency) / 24)}/mo</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <LandingFeatureGrid />
      <LandingCTABand />

      <Footer />
    </div>
  );
};

export default ProductLine;
